import { ListProps, TextField, useResourceContext } from 'react-admin';
import { useRbacList } from '@hooks';
import {
  DateField,
  DateRangeSelect,
  Forbidden,
  Grid,
  GridList,
  Loader,
  MoneyField,
  SelectArrayInput,
  TextInput,
} from '@components';
import { TextPostfixField } from '@components/fields/TextPostfixField';
import { riskDataHttp, RiskDataSettings } from '@network';
import { useEffect, useState } from 'react';
import { CreditStatusField, creditStatusOptions } from '@pages/credit/CreditStatusField';
import { makeExporter } from '@hooks/use-exporter';

export const RiskDataList = (props: ListProps) => {
  const rbacList = useRbacList();
  const resource = useResourceContext();
  const [settings, setSettings] = useState<RiskDataSettings>();
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const nextSettings = await riskDataHttp.getRiskDataSettings();
      setSettings(nextSettings);
      setLoading(false);
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  const exporter = makeExporter({
    resource,
    filename: 'Risk Data',
    props: {
      'credit.code': {
        label: 'Credit code',
      },
      'credit.accreditedName': {
        label: 'Accredited',
      },
      'credit.status': {
        label: 'Credit status',
        prop: 'credit.statusEn',
      },
      'credit.bankApproval': {
        label: 'Credit amount',
        transform: 'money',
      },
      'credit.signatureAt': {
        label: 'Signature date',
        transform: 'date',
      },
      'credit.paymentAt': {
        label: 'Payment date',
        transform: 'date',
      },
      ...settings?.columns.reduce((acc, key) => {
        const isFinancial = settings.financialColumns.includes(key);
        const isXPostfix = settings.xPostfixColumns.includes(key);
        acc[key] = {
          label: key,
          transform: isFinancial ? 'money' : undefined,
          postfix: isXPostfix ? 'x' : undefined,
        };
        return acc;
      }, {} as Record<string, {label: string; transform?: 'money' | 'date'; postfix?: string}>),
    },
  });

  return isLoading || !settings ? <Loader mini center /> : (
    <GridList
      {...props}
      filters={Filters}
      sort={{ field: 'id', order: 'DESC' }}
      exporter={exporter}
      hideRemoveButton
    >
      <Grid
        rowClick={false}
        defaultColumns={['credit.code', 'credit.accreditedName', 'credit.status', 'credit.bankApproval',
          'credit.signatureAt', 'credit.paymentAt', ...settings.columns,
        ]}
      >
        <TextField source="credit.code" label="Credit code" />
        <TextField source="credit.accreditedName" label="Accredited" />
        <CreditStatusField source="credit.status" label="Credit status" />
        <MoneyField source="credit.bankApproval"
          label="Credit amount"
          style={{ display: 'inline-block', width: 100 }} />
        <DateField source="credit.signatureAt" label="Signature date" />
        <DateField source="credit.paymentAt" label="Payment date" />

        {settings.columns.map((key) => (
          settings.financialColumns.includes(key)
            ? <MoneyField key={key} source={key} label={key} nullable />
            : settings.xPostfixColumns.includes(key)
              ? <TextPostfixField key={key} source={key} label={key} postfix="x" />
              : <TextField key={key} source={key} label={key} />
        ))}
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" label="Search" alwaysOn style={{ width: 200 }} />,
  <SelectArrayInput
    key="status"
    source="status"
    choices={creditStatusOptions}
    className="MS-field"
    style={{ width: 250 }}
    alwaysOn
  />,
  <DateRangeSelect
    key="createdAt"
    source="createdAt"
    label="Signature At/Payment At"
    alwaysOn
    choices={[
      { id: 'signatureAt', name: 'Signature At' },
      { id: 'paymentAt', name: 'Payment At' },
    ]}
  />,
];
